const DAY_TIME = 24 * 60 * 60 * 1000;

export function toUtcDayTimestamp(value) {
  if (!value) return 0;

  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }

  const raw = typeof value === 'number' && value < 1e12 ? value * 1000 : value;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return 0;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

export function toUtcDaySeconds(value) {
  const timestamp = toUtcDayTimestamp(value);
  return timestamp > 0 ? Math.floor(timestamp / 1000) : 0;
}

function parseDateOnly(value) {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day).getTime();
}

function toTimestamp(value) {
  const dateOnlyTs = parseDateOnly(value);
  if (Number.isFinite(dateOnlyTs)) return dateOnlyTs;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function normalizePeriods(input) {
  if (!Array.isArray(input)) return [];

  const sorted = [...input]
    .filter((item) => item && item.start_date && item.end_date)
    .sort((a, b) => {
      const numberDiff = Number(a.number || 0) - Number(b.number || 0);
      if (numberDiff !== 0) return numberDiff;
      return toTimestamp(a.start_date) - toTimestamp(b.start_date);
    });

  const uniq = [];
  const seen = new Set();
  sorted.forEach((period) => {
    const key = Number(period.number || 0);
    if (seen.has(key)) return;
    seen.add(key);
    uniq.push(period);
  });
  return uniq;
}

function findOrCreateMonth(months, day) {
  let month = months.find((item) => item.year === day.year && item.month === day.month);
  if (!month) {
    month = {
      year: day.year,
      month: day.month,
      weeks: []
    };
    months.push(month);
  }
  return month;
}

export function parsePeriodsLegacy(rawPeriods) {
  const days = [];
  const weeks = [];
  const months = [];
  const quarters = [];
  const holidays = [];

  const periods = normalizePeriods(rawPeriods);
  periods.forEach((period, index) => {
    let week = null;
    const quarterNumber = Number(period.number || index + 1);

    let start = toTimestamp(period.start_date);
    const endRaw = toTimestamp(period.end_date);
    if (!Number.isFinite(start) || !Number.isFinite(endRaw)) return;

    const weekday = new Date(start).getDay();
    if (weekday === 0) start += DAY_TIME;
    if (weekday === 6) start += DAY_TIME * 2;

    let end = endRaw;
    const nextPeriod = periods[index + 1];
    if (nextPeriod) {
      const nextStart = toTimestamp(nextPeriod.start_date);
      if (Number.isFinite(nextStart) && end === nextStart) {
        end -= DAY_TIME;
      }
    }

    quarters.push({
      start,
      end,
      number: quarterNumber,
      first_week: period.first_week
    });

    if (nextPeriod?.start_date) {
      holidays.push({
        start: period.end_date,
        end: nextPeriod.start_date
      });
    }

    for (let ts = start; ts <= end; ts += DAY_TIME) {
      const date = new Date(ts);
      const day = {
        date: ts,
        number: date.getDay(),
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        quarter: quarterNumber
      };
      days.push(day);

      if (day.number === 1) {
        if (week) {
          week.start = week.days[0].date;
          week.end = week.days[week.days.length - 1].date + DAY_TIME;
          weeks.push(week);
        }
        week = null;
      }

      if (!week) week = { days: [] };
      day.week = week;
      week.days.push(day);
    }

    if (week?.days?.length) {
      week.start = week.days[0].date;
      week.end = week.days[week.days.length - 1].date + DAY_TIME;
      weeks.push(week);
    }
  });

  weeks.forEach((week) => {
    const first = week.days[0];
    const last = week.days[week.days.length - 1];
    week.month = last.month;
    week.quarter = last.quarter;

    const firstMonth = findOrCreateMonth(months, first);
    firstMonth.weeks.push(week);
    if (first.month !== last.month) {
      const lastMonth = findOrCreateMonth(months, last);
      lastMonth.weeks.push(week);
    }
  });

  return {
    start: days.length ? days[0].date : null,
    end: days.length ? days[days.length - 1].date : null,
    quarters,
    days,
    weeks,
    months,
    holidays
  };
}

function buildContinuousTimeline(startBound, endBound, parsedDays) {
  if (!Number.isFinite(startBound) || !Number.isFinite(endBound) || startBound > endBound) {
    return null;
  }

  const sourceDayMap = new Map((parsedDays || []).map((day) => [day.date, day]));
  const days = [];
  const weeks = [];
  const months = [];

  let week = null;
  for (let ts = startBound; ts <= endBound; ts += DAY_TIME) {
    const date = new Date(ts);
    const sourceDay = sourceDayMap.get(ts);

    const day = {
      date: ts,
      number: date.getDay(),
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      quarter: sourceDay?.quarter || 0
    };
    days.push(day);

    if (day.number === 1) {
      if (week) {
        week.start = week.days[0].date;
        week.end = week.days[week.days.length - 1].date + DAY_TIME;
        weeks.push(week);
      }
      week = null;
    }

    if (!week) week = { days: [] };
    day.week = week;
    week.days.push(day);
  }

  if (week?.days?.length) {
    week.start = week.days[0].date;
    week.end = week.days[week.days.length - 1].date + DAY_TIME;
    weeks.push(week);
  }

  weeks.forEach((weekItem) => {
    const first = weekItem.days[0];
    const last = weekItem.days[weekItem.days.length - 1];
    weekItem.month = last.month;
    weekItem.quarter = last.quarter;

    const firstMonth = findOrCreateMonth(months, first);
    firstMonth.weeks.push(weekItem);
    if (first.month !== last.month) {
      const lastMonth = findOrCreateMonth(months, last);
      lastMonth.weeks.push(weekItem);
    }
  });

  return { days, weeks, months };
}

function capitalize(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildDayTitle(date, locale) {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
}

function buildDaySubtitle(date, locale) {
  const text = date.toLocaleDateString(locale, { weekday: 'long' });
  return capitalize(text);
}

function nearestDay(days, preferredTs) {
  if (!days.length) return null;
  const target = Number(preferredTs);
  const targetKey = toPeriodDateKey(target);
  const exactByCalendarDate = days.find((day) => toPeriodDateKey(day.date) === targetKey);
  if (exactByCalendarDate) return exactByCalendarDate;

  const exact = days.find((day) => day.date === target);
  if (exact) return exact;

  let nearest = days[0];
  let minDiff = Math.abs(days[0].date - target);
  for (let i = 1; i < days.length; i += 1) {
    const diff = Math.abs(days[i].date - target);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = days[i];
    }
  }
  return nearest;
}

export function buildPeriodSliderData(rawPeriods, options = {}) {
  const locale = options.locale || 'ru-RU';
  const periodsArray = Array.isArray(rawPeriods) ? rawPeriods : rawPeriods?.periods || [];
  const parsed = parsePeriodsLegacy(periodsArray);

  const quarterOne = parsed.quarters.find((quarter) => quarter.number === 1);
  const quarterFour = parsed.quarters.find((quarter) => quarter.number === 4);

  const startBound = quarterOne?.start ?? parsed.start;
  const endBound = quarterFour?.end ?? parsed.end;

  const continuous = buildContinuousTimeline(startBound, endBound, parsed.days);
  const days = continuous?.days || parsed.days.filter((day) => {
    if (!Number.isFinite(startBound) || !Number.isFinite(endBound)) return true;
    return day.date >= startBound && day.date <= endBound;
  });
  const weeks = continuous?.weeks || parsed.weeks.filter((week) => {
    if (!Number.isFinite(startBound) || !Number.isFinite(endBound)) return true;
    return week.start >= startBound && week.start <= endBound;
  });
  const months = continuous?.months || parsed.months.filter((month) =>
    month.weeks.some((week) => weeks.includes(week))
  );

  const dayItems = days.map((day) => {
    const date = new Date(day.date);
    return {
      title: buildDayTitle(date, locale),
      subtitle: buildDaySubtitle(date, locale),
      day
    };
  });

  const weekItems = weeks.map((week) => {
    const start = new Date(week.start);
    const end = new Date(week.end - DAY_TIME);
    const sameMonth = start.getMonth() === end.getMonth();
    const startFormat = sameMonth
      ? start.toLocaleDateString(locale, { day: 'numeric' })
      : buildDayTitle(start, locale);
    const endFormat = buildDayTitle(end, locale);
    return {
      title: `${startFormat} - ${endFormat}`,
      subtitle: '',
      week
    };
  });

  const monthItems = months.map((month) => {
    const date = new Date(month.year, month.month, 1);
    return {
      title: capitalize(date.toLocaleDateString(locale, { month: 'long' })),
      subtitle: '',
      month,
      year: month.year,
      start: date
    };
  });

  const preferredTs = Number(options.selectedTs || Date.now());
  const initialDay = nearestDay(days, preferredTs);
  const initialWeek = initialDay ? weekItems.find((item) => item.week === initialDay.week) || weekItems[0] : weekItems[0] || null;
  const initialMonth =
    initialWeek
      ? monthItems.find((item) => item.month?.month === initialWeek.week?.month && item.month?.year === new Date(initialWeek.week.start).getFullYear())
      : monthItems[0] || null;

  return {
    parsed,
    bounds: {
      start: startBound,
      end: endBound
    },
    days: {
      items: dayItems,
      current: initialDay ? dayItems.find((item) => item.day === initialDay) || null : null
    },
    weeks: {
      items: weekItems,
      current: initialWeek
    },
    months: {
      items: monthItems,
      current: initialMonth,
      source: months
    }
  };
}

export function toPeriodDateKey(value) {
  const date = new Date(value);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
